import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  Receipt as TicketIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import PaginatedTable from '../components/common/PaginatedTable';
import { ticketsApi } from '../api/tickets.api';
import { Ticket, TicketStatus } from '../types';
import LoadingSpinner from '../components/common/LoadingSpinner';

const STATUS_META: Record<TicketStatus, { label: string; color: 'success' | 'warning' | 'error' }> = {
  impreso: { label: 'Impreso', color: 'success' },
  sin_impresora: { label: 'Sin impresora', color: 'warning' },
  error: { label: 'Error', color: 'error' },
};

const Tickets: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'' | TicketStatus>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [viewing, setViewing] = useState<Ticket | null>(null);
  const [reprinting, setReprinting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ticketsApi.getAll({
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
      });
      setTickets(res.data);
    } catch (err) {
      console.error('Error loading tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [status, from, to]);

  useEffect(() => { load(); }, [load]);

  const handleReprint = async (t: Ticket) => {
    setReprinting(true);
    try {
      const res = await ticketsApi.reprint(t.id);
      if (res.data.printed) {
        alert(`Ticket #${t.id} reenviado a la impresora de ${t.stationName ?? 'la estacion'}.`);
      } else {
        const reason = res.data.reason === 'no-printer-configured'
          ? 'La estacion no tiene impresora configurada.'
          : res.data.reason === 'ticket-sin-estacion'
            ? 'Este ticket no esta asociado a una estacion con impresora.'
            : res.data.reason || 'desconocido';
        alert(`No se pudo reimprimir: ${reason}`);
      }
      load();
    } catch (err: any) {
      alert(`Error: ${err?.message || err}`);
    } finally {
      setReprinting(false);
    }
  };

  const fmtDate = (iso: string) => new Date(iso).toLocaleString('es-PE');

  if (loading && tickets.length === 0) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TicketIcon sx={{ fontSize: 32, color: '#4F46E5' }} />
          <Typography variant="h4">Tickets de Comanda</Typography>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
        Cada vez que un mozo envia un pedido se genera y guarda un ticket por estacion (bar/cocina).
        Aqui puedes consultarlos y reimprimirlos.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          label="Estado"
          size="small"
          value={status}
          onChange={(e) => setStatus(e.target.value as '' | TicketStatus)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="impreso">Impreso</MenuItem>
          <MenuItem value="sin_impresora">Sin impresora</MenuItem>
          <MenuItem value="error">Error</MenuItem>
        </TextField>
        <TextField
          type="date"
          label="Desde"
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          label="Hasta"
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      <PaginatedTable
        columns={[
          { id: 'id', label: 'ID', minWidth: 60 },
          { id: 'orderCode', label: 'Pedido', sx: { fontWeight: 600 } },
          {
            id: 'stationType', label: 'Estacion',
            format: (_v: any, row: Ticket) => {
              const t = row.stationType;
              const name = row.stationName || (t === 'bar' ? 'Bar' : t === 'cocina' ? 'Cocina' : 'Sin estacion');
              return (
                <Chip
                  label={name}
                  size="small"
                  color={t === 'bar' ? 'info' : t === 'cocina' ? 'warning' : 'default'}
                />
              );
            },
          },
          {
            id: 'tableNumber', label: 'Mesa',
            format: (v: string) => v || <span style={{ color: '#9CA3AF' }}>—</span>,
          },
          {
            id: 'waiterName', label: 'Mozo',
            format: (v: string) => v || <span style={{ color: '#9CA3AF' }}>—</span>,
          },
          {
            id: 'items', label: 'Items',
            format: (_v: any, row: Ticket) => (row.items?.length ?? 0),
          },
          {
            id: 'status', label: 'Estado',
            format: (v: TicketStatus) => {
              const m = STATUS_META[v] ?? { label: v, color: 'default' as const };
              return <Chip label={m.label} size="small" color={m.color as any} />;
            },
          },
          {
            id: 'createdAt', label: 'Fecha',
            format: (v: string) => fmtDate(v),
          },
        ]}
        rows={tickets}
        searchFields={['orderCode', 'stationName', 'waiterName', 'tableNumber']}
        exportFileName="tickets"
        emptyMessage="No hay tickets generados todavia."
        renderActions={(t: Ticket) => (
          <>
            <IconButton size="small" color="primary" onClick={() => setViewing(t)} title="Ver ticket">
              <ViewIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="success" onClick={() => handleReprint(t)} title="Reimprimir">
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        )}
      />

      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Ticket #{viewing?.id} — {viewing?.orderCode}
        </DialogTitle>
        <DialogContent>
          {viewing && (
            <>
              <Box sx={{ mb: 1 }}>
                <Chip
                  label={(STATUS_META[viewing.status] ?? { label: viewing.status }).label}
                  size="small"
                  color={(STATUS_META[viewing.status]?.color ?? 'default') as any}
                />
                {viewing.printCount > 0 && (
                  <Typography variant="caption" sx={{ ml: 1, color: '#6B7280' }}>
                    Impreso {viewing.printCount} {viewing.printCount === 1 ? 'vez' : 'veces'}
                  </Typography>
                )}
              </Box>
              <Box
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 13,
                  whiteSpace: 'pre-wrap',
                  bgcolor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: 1,
                  p: 2,
                  m: 0,
                }}
              >
                {viewing.content}
              </Box>
              {viewing.printError && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  Ultimo error: {viewing.printError}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setViewing(null)}>Cerrar</Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            disabled={reprinting}
            onClick={() => viewing && handleReprint(viewing)}
          >
            Reimprimir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tickets;
